/**
 * Who the subscribe dialog stops at the door (TMMAPP-0116). Read from what `/me` and `/me/billing`
 * already serve; it is not a contract field.
 *
 * - `open`: a SUBSCRIBER or a GUEST. No dialog on arrival (a guest buys from `offer` mode).
 * - `blocking`: locked (past grace; a GUEST keeps the guest's access), or NONE with no
 *   subscription ever served — the blocking dialog over the empty shell. Settings and `/auth/*`
 *   stay reachable (Operator, 2026-10-08; TMMAPP-0117 B13, TMMAPP-0118 C12). A parked
 *   re-subscribe opens the blocking dialog in its resume state: the dialog reads
 *   `/me/billing.pending` itself.
 * - `grace`: NONE, not locked, and they own a virtual account or `/me/billing` serves a
 *   subscription (a lapsed subscriber whose access continues read-only). The dismissible dialog
 *   opens once per session.
 * - `resume`: in grace with a re-subscribe in progress (`/me/billing.pending`, TMMAPP-0117 B19):
 *   the dismissible dialog opens on every arrival in its resume state (finish the challenge, or
 *   "setting up"), never the grace offer.
 * - `graceReading`: an owner in grace while `/me/billing` reads. The route renders at once (a
 *   loaded session never blanks); no dialog until the read picks `resume` or `grace` (an
 *   unreadable read is `grace`).
 * - `checking`: NONE, not locked and owning nothing; `/me/billing` decides. The empty shell
 *   meanwhile.
 *
 * Grace and past grace are never read from `subscription.status`: `/me/billing.subscription` is the
 * newest row for anyone who ever subscribed, and `pending` is what says a re-subscribe is parked.
 *
 * A NONE person who isn't locked has their shares reconciled once per signed-in session before the
 * gate decides (TMMAPP-0158): an accepted invitation starts a guest seat, `/me` is re-read, and a
 * GUEST goes in, whatever route they arrived on. Meanwhile the gate is `checking` (or `graceReading`
 * for an owner in grace). The flag is in memory only.
 */
import { useEffect, useState } from 'react';
import { billingApi } from '@/api/billing';
import type { Me } from '@/api/me';
import { reconcileShares } from '@/features/sharing/reconcileShares';
import { useMe, useMeRefetch } from './meHooks';

export type SubscribeGate = 'open' | 'blocking' | 'grace' | 'resume' | 'graceReading' | 'checking';

/** The gate `/me` alone decides; `null` when `/me/billing` has to. An owner's `grace` is provisional. */
export function gateFromMe(me: Me): 'open' | 'blocking' | 'grace' | null {
  // Locked is past grace whatever the tier says; a guest seat elsewhere keeps the guest's access.
  if (me.locked && me.tier !== 'GUEST') return 'blocking';
  if (me.tier !== 'NONE') return 'open';
  if (me.virtualAccounts.some((row) => row.mine)) return 'grace';
  return null;
}

/** What `/me/billing` decides for a NONE person who isn't locked. */
interface Served {
  subscribed: boolean;
  pending: boolean;
}

/** This session's one automatic reconcile: started at most once, settled once it answered. */
let sessionReconcile: Promise<void> | null = null;
let sessionReconcileSettled = false;

function reconcileOncePerSession(refetch: () => Promise<Me | void>): Promise<void> {
  // `/me` is re-read only when a share was bound; any failure is "nothing to reconcile".
  sessionReconcile ??= reconcileShares(refetch, (answer) => answer.reconciled > 0).then(() => {
    sessionReconcileSettled = true;
  });
  return sessionReconcile;
}

/**
 * When this session last ran "Check again" (`InvitationCheck`), in memory beside the reconcile flag
 * and never in storage, so its once-a-minute cooldown survives the dialog's remount (TMMAPP-0160
 * item 8).
 */
let lastInvitationCheckAt: number | null = null;

/** The milliseconds left of "Check again"'s cooldown at `now`; 0 when it may run. */
export function invitationCheckWait(cooldownMs: number, now = Date.now()): number {
  if (lastInvitationCheckAt === null) return 0;
  return Math.max(0, lastInvitationCheckAt + cooldownMs - now);
}

/** Records a "Check again" run at `now`. */
export function markInvitationCheck(now = Date.now()): void {
  lastInvitationCheckAt = now;
}

/** Test seam: forget this session's automatic reconcile and its last "Check again". */
export function __resetSessionReconcile(): void {
  sessionReconcile = null;
  sessionReconcileSettled = false;
  lastInvitationCheckAt = null;
}

export function useSubscribeGate(): SubscribeGate {
  const me = useMe();
  const refetch = useMeRefetch();
  const direct = gateFromMe(me);
  // A NONE person who isn't locked (a locked NONE is `blocking` above): their shares first.
  const wantsReconcile = me.tier === 'NONE' && !me.locked;
  const [reconciled, setReconciled] = useState(() => sessionReconcileSettled);
  const reconciling = wantsReconcile && !reconciled;
  // An owner in grace reads it too: only `pending` tells grace from a parked re-subscribe.
  const needsBilling = direct === null || direct === 'grace';
  // `null` while reading, `'unreadable'` when the read failed.
  const [served, setServed] = useState<Served | 'unreadable' | null>(null);

  useEffect(() => {
    if (!reconciling) return;
    let cancelled = false;
    void reconcileOncePerSession(refetch).then(() => {
      if (!cancelled) setReconciled(true);
    });
    return () => {
      cancelled = true;
    };
  }, [reconciling, refetch]);

  useEffect(() => {
    if (!needsBilling || served !== null) return;
    let cancelled = false;
    void billingApi
      .profile()
      .then((profile) => {
        if (!cancelled) {
          setServed({ subscribed: profile.subscription != null, pending: profile.pending != null });
        }
      })
      .catch(() => {
        if (!cancelled) setServed('unreadable');
      });
    return () => {
      cancelled = true;
    };
  }, [needsBilling, served]);

  if (direct === 'grace') {
    if (served === null || reconciling) return 'graceReading';
    return served !== 'unreadable' && served.pending ? 'resume' : 'grace';
  }
  if (direct !== null) return direct;
  if (served === null || reconciling) return 'checking';
  // Unreadable billing for someone who owns nothing: treated as never subscribed (the dialog is the
  // way in either way).
  if (served === 'unreadable') return 'blocking';
  if (served.subscribed) return served.pending ? 'resume' : 'grace';
  // Never subscribed: a first attempt still parked resumes inside the blocking dialog.
  return 'blocking';
}
